import React, { useEffect, useState, Component } from 'react';
import { Check, Clock, ShieldCheck, AlertCircle, ArrowRight, Star, ChevronDown, X } from 'lucide-react';
// Modal Component
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-2"></div>
      <div className="relative w-full max-w-2xl max-h-[80vh] bg-white rounded-xl shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-200" data-id="element-3">
        <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-4">
          <h3 className="text-xl font-bold text-[#1C1917]" data-id="element-5">{title}</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-6">
            <X className="w-5 h-5" data-id="element-7" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto text-gray-600 leading-relaxed space-y-4" data-id="element-8">
          {children}
        </div>
      </div>
    </div>;
}
export function SalesPage() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <div className="min-h-screen bg-[#FAFAF8] text-[#1C1917]" data-id="element-9">
      {/* Top Banner */}
      <div className="bg-[#1C1917] text-white text-center py-2 text-sm font-medium tracking-wide" data-id="element-10">
        ATENCIÓN: Solo para personas con Fascitis Plantar Crónica
      </div>

      {/* Hero Section */}
      <header className="pt-12 pb-16 px-4 md:px-6 lg:px-8 max-w-5xl mx-auto text-center" data-id="element-11">
        <div className="inline-flex items-center gap-2 bg-[#F5F0E8] px-4 py-1.5 rounded-full text-[#EA580C] font-semibold text-sm mb-8 border border-[#EA580C]/20" data-id="element-12">
          <Star className="w-4 h-4 fill-current" data-id="element-13" />
          <span data-id="element-14">Nuevo "Ritual de Cama" Para el Dolor de Talón</span>
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-6 text-[#1C1917]" data-id="element-15">
          Detén el "Clavo Ardiente": <br className="hidden md:block" data-id="element-16" />
          <span className="text-[#EA580C]" data-id="element-17">
            Elimina tu Fascitis Plantar
          </span>{' '}
          Antes de que tus Pies Toquen el Suelo.
        </h1>

        <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-18">
          El ritual de 7 minutos en cama que está poniendo nerviosos a los
          podólogos — y devolviendo las mañanas sin dolor a miles de personas.
        </p>

        <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-16" data-id="element-19">
          <div className="relative w-full max-w-md shadow-2xl rounded-xl overflow-hidden border-4 border-white transform rotate-1 hover:rotate-0 transition-transform duration-500" data-id="element-20">
            <img src="https://cdn.magicpatterns.com/uploads/rCN4w4GPuHQrqmkwexaCwt/fp1.png" alt="La Solución de 7 Minutos para el Talón Matutino en Tablet" className="w-full h-auto" data-id="element-21" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4" data-id="element-22">
              <p className="text-white font-medium text-sm" data-id="element-23">
                Descarga Digital Instantánea
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 w-full max-w-sm" data-id="element-24">
            <button className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white text-xl font-bold py-5 px-8 rounded-lg shadow-lg shadow-[#EA580C]/30 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3 group" onClick={() => document.getElementById('oferta')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-25">
              Obtener la Solución de 7 Minutos
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-26" />
            </button>
            <p className="text-sm text-gray-500 flex items-center justify-center gap-2" data-id="element-27">
              <ShieldCheck className="w-4 h-4 text-[#0D9488]" data-id="element-28" />
              Garantía de Devolución del 100%
            </p>
          </div>
        </div>
      </header>

      {/* Problem Section */}
      <section className="bg-[#F5F0E8] py-20 px-4 md:px-6" data-id="element-29">
        <div className="max-w-3xl mx-auto" data-id="element-30">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" data-id="element-31">
            ¿Tienes miedo de dar ese "primer paso" al despertar?
          </h2>

          <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-32">
            <p data-id="element-33">
              Si sufres de fascitis plantar, conoces ese sentimiento a la
              perfección.
            </p>
            <p data-id="element-34">
              Te despiertas. Te sientes bien por un momento... y luego tus pies
              tocan el suelo.
            </p>
            <div className="bg-white p-6 rounded-lg border-l-4 border-[#EA580C] shadow-sm my-8" data-id="element-35">
              <p className="font-medium italic text-[#1C1917]" data-id="element-36">
                "Un choque eléctrico. Un pinchazo agudo. Como pisar un clavo
                ardiente con el talón descalzo."
              </p>
            </div>
            <p data-id="element-37">
              Te han dicho que compres plantillas ortopédicas caras. Te han
              dicho que duermas con botas ortopédicas incómodas que arruinan tu
              descanso. Incluso te han dicho que quizás necesites inyecciones de
              cortisona dolorosas.
            </p>
            <p className="font-bold text-[#1C1917]" data-id="element-38">
              Pero lo que no te han dicho es que estás re-lesionando tu pie cada
              mañana.
            </p>
            <p data-id="element-39">
              Cuando te levantas sin preparar tu fascia, estás desgarrando los
              micro-tejidos que tu cuerpo intentó sanar durante la noche. Por
              eso el dolor no desaparece. Por eso cada mañana es una batalla.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 md:px-6 max-w-4xl mx-auto" data-id="element-40">
        <div className="text-center mb-16" data-id="element-41">
          <span className="text-[#0D9488] font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-42">
            Presentamos
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-43">
            La Solución de 7 Minutos para el Talón Matutino
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-44">
            No es un libro de ejercicios aburridos. Es un protocolo de
            "activación térmica" llamado{' '}
            <strong className="text-[#EA580C]" data-id="element-45">Isocore Stretching™</strong> que
            realizas <em data-id="element-46">bajo las sábanas</em>.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-47">
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center" data-id="element-48">
            <div className="w-12 h-12 bg-[#F5F0E8] rounded-full flex items-center justify-center mx-auto mb-4 text-[#EA580C]" data-id="element-49">
              <Clock className="w-6 h-6" data-id="element-50" />
            </div>
            <h3 className="font-bold text-xl mb-3" data-id="element-51">Solo 7 Minutos</h3>
            <p className="text-gray-600" data-id="element-52">
              Completamente en cama. Sin equipamiento. Prepara el tejido antes
              de cargar peso.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center" data-id="element-53">
            <div className="w-12 h-12 bg-[#F5F0E8] rounded-full flex items-center justify-center mx-auto mb-4 text-[#EA580C]" data-id="element-54">
              <AlertCircle className="w-6 h-6" data-id="element-55" />
            </div>
            <h3 className="font-bold text-xl mb-3" data-id="element-56">Detén el Desgarro</h3>
            <p className="text-gray-600" data-id="element-57">
              Activa el flujo sanguíneo y "desbloquea" el arco para no desgarrar
              el tejido sanado.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center" data-id="element-58">
            <div className="w-12 h-12 bg-[#F5F0E8] rounded-full flex items-center justify-center mx-auto mb-4 text-[#EA580C]" data-id="element-59">
              <Check className="w-6 h-6" data-id="element-60" />
            </div>
            <h3 className="font-bold text-xl mb-3" data-id="element-61">Regla Cero Suelo</h3>
            <p className="text-gray-600" data-id="element-62">
              Un simple cambio de hábito que protege tus arcos en el momento en
              que dejas la cama.
            </p>
          </div>
        </div>

        {/* What's Included */}
        <div id="oferta" className="bg-[#1C1917] text-white rounded-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden" data-id="element-63">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#EA580C] opacity-10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" data-id="element-64"></div>

          <div className="relative z-10" data-id="element-65">
            <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center" data-id="element-66">
              Lo Que Obtienes Hoy
            </h3>

            <div className="grid md:grid-cols-2 gap-y-6 gap-x-12 mb-10" data-id="element-67">
              <ul className="space-y-4" data-id="element-68">
                <li className="flex items-start gap-3" data-id="element-69">
                  <Check className="w-6 h-6 text-[#0D9488] flex-shrink-0 mt-0.5" data-id="element-70" />
                  <span data-id="element-71">
                    <strong data-id="element-72">El Método Isocore</strong> (Guía Visual Paso a Paso)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-73">
                  <Check className="w-6 h-6 text-[#0D9488] flex-shrink-0 mt-0.5" data-id="element-74" />
                  <span data-id="element-75">
                    <strong data-id="element-76">La Regla "Cero Suelo"</strong> para proteger tus
                    arcos en casa
                  </span>
                </li>
              </ul>
              <ul className="space-y-4" data-id="element-77">
                <li className="flex items-start gap-3" data-id="element-78">
                  <Check className="w-6 h-6 text-[#0D9488] flex-shrink-0 mt-0.5" data-id="element-79" />
                  <span data-id="element-80">
                    <strong data-id="element-81">Checklist de Recuperación de 21 Días</strong> para
                    monitorear tu progreso
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-82">
                  <Check className="w-6 h-6 text-[#0D9488] flex-shrink-0 mt-0.5" data-id="element-83" />
                  <span data-id="element-84">
                    <strong data-id="element-85">BONO: Guía de Calzado</strong> — Deja de gastar
                    dinero en zapatos que te lastiman
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-white/5 rounded-xl p-6 text-center border border-white/10" data-id="element-86">
              <p className="text-gray-400 text-sm mb-2 uppercase tracking-widest" data-id="element-87">
                Oferta Única
              </p>
              <div className="flex items-center justify-center gap-4 mb-6" data-id="element-88">
                <span className="text-4xl font-bold text-white" data-id="element-89">$6.39</span>
                <span className="text-xl text-gray-500 line-through" data-id="element-90">
                  $14.99
                </span>
              </div>
              <a href="https://shop.app/checkout/70480724155/cn/hWN8QFNgibCSrhNLACnPKhxx/en-us/shoppay?_cs=3.AMPS&_r=AQABCmlFwu7GSPYnD-XaQm-XYFovGfCj57tfQn1rjQkJG54&cart_link_id=htHxUeYf&redirect_source=checkout_automatic_redirect&tracking_unique=16510270-6716-4694-861d-26d94c7817f8&tracking_visit=83b7185f-0022-4FFF-1FBE-041C46F23392" target="_blank" rel="noopener noreferrer" className="w-full md:w-auto bg-[#EA580C] hover:bg-[#C2410C] text-white text-lg font-bold py-4 px-12 rounded-lg shadow-lg shadow-[#EA580C]/30 transition-all transform hover:-translate-y-1 inline-block text-center" data-id="element-91">
                Descargar Ahora y Detener el Dolor
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="bg-[#F5F0E8] py-16 px-4 text-center" data-id="element-92">
        <div className="max-w-2xl mx-auto" data-id="element-93">
          <ShieldCheck className="w-16 h-16 text-[#0D9488] mx-auto mb-6" data-id="element-94" />
          <h3 className="text-2xl font-bold mb-4" data-id="element-95">
            Garantía de 60 Días "Mañanas Sin Dolor"
          </h3>
          <p className="text-gray-600 mb-8" data-id="element-96">
            Prueba el método Isocore Stretching™ durante 60 días. Si no
            despiertas con significativamente menos dolor, escríbenos y te
            devolvemos cada centavo. Sin preguntas. Sin complicaciones. Y te
            quedas con la guía.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 max-w-3xl mx-auto" data-id="element-97">
        <h3 className="text-2xl font-bold mb-8 text-center" data-id="element-98">
          Preguntas Frecuentes
        </h3>
        <div className="space-y-4" data-id="element-99">
          <details className="group bg-white rounded-lg border border-gray-200 p-4 [&_summary::-webkit-details-marker]:hidden" data-id="element-100">
            <summary className="flex cursor-pointer items-center justify-between gap-1.5 font-medium text-gray-900" data-id="element-101">
              <h4 className="text-lg" data-id="element-102">¿Es un libro físico?</h4>
              <ChevronDown className="h-5 w-5 shrink-0 transition duration-300 group-open:-rotate-180" data-id="element-103" />
            </summary>
            <p className="mt-4 leading-relaxed text-gray-700" data-id="element-104">
              No, es una guía digital (PDF) que puedes descargar al instante en
              tu teléfono, tablet u ordenador. Puedes comenzar tu rutina de
              alivio mañana mismo por la mañana.
            </p>
          </details>

          <details className="group bg-white rounded-lg border border-gray-200 p-4 [&_summary::-webkit-details-marker]:hidden" data-id="element-105">
            <summary className="flex cursor-pointer items-center justify-between gap-1.5 font-medium text-gray-900" data-id="element-106">
              <h4 className="text-lg" data-id="element-107">¿Necesito algún equipamiento?</h4>
              <ChevronDown className="h-5 w-5 shrink-0 transition duration-300 group-open:-rotate-180" data-id="element-108" />
            </summary>
            <p className="mt-4 leading-relaxed text-gray-700" data-id="element-109">
              Para nada. El método Isocore Stretching™ está diseñado para
              realizarse en tu cama, usando únicamente el peso y el movimiento
              de tu propio cuerpo.
            </p>
          </details>

          <details className="group bg-white rounded-lg border border-gray-200 p-4 [&_summary::-webkit-details-marker]:hidden" data-id="element-110">
            <summary className="flex cursor-pointer items-center justify-between gap-1.5 font-medium text-gray-900" data-id="element-111">
              <h4 className="text-lg" data-id="element-112">¿Funcionará si tengo el pie plano?</h4>
              <ChevronDown className="h-5 w-5 shrink-0 transition duration-300 group-open:-rotate-180" data-id="element-113" />
            </summary>
            <p className="mt-4 leading-relaxed text-gray-700" data-id="element-114">
              ¡Sí! El método se enfoca en el tejido de la fascia en sí,
              independientemente de la altura de tu arco. Ayuda a liberar la
              tensión que se acumula durante la noche tanto en pies planos como
              en arcos altos.
            </p>
          </details>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1C1917] text-gray-400 py-12 px-4 text-center text-sm" data-id="element-115">
        <div className="max-w-4xl mx-auto" data-id="element-116">
          <p className="mb-4" data-id="element-117">
            &copy; {new Date().getFullYear()} Morning Heel Fix. Todos los
            derechos reservados.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-6 text-xs font-medium" data-id="element-118">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors underline underline-offset-4" data-id="element-119">
              Política de Privacidad
            </button>
            <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors underline underline-offset-4" data-id="element-120">
              Términos y Condiciones
            </button>
          </div>
          <p className="max-w-2xl mx-auto text-xs text-gray-500" data-id="element-121">
            Aviso legal: El contenido de esta guía es solo para fines
            informativos y no pretende sustituir el consejo, diagnóstico o
            tratamiento médico profesional. Consulta siempre con tu médico u
            otro profesional de la salud cualificado ante cualquier pregunta
            sobre una condición médica.
          </p>
        </div>
      </footer>

      {/* Modals */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-122">
        <div className="space-y-4" data-id="element-123">
          <p data-id="element-124">
            <strong data-id="element-125">
              Última actualización: {new Date().toLocaleDateString()}
            </strong>
          </p>
          <p data-id="element-126">
            En Morning Heel Fix, respetamos su privacidad y nos comprometemos a
            proteger sus datos personales. Esta política describe cómo
            recopilamos y utilizamos su información.
          </p>

          <h4 className="font-bold text-gray-900 mt-4" data-id="element-127">
            1. Qué datos recopilamos
          </h4>
          <p data-id="element-128">
            Recopilamos información que usted nos proporciona directamente, como
            su nombre y dirección de correo electrónico cuando realiza una
            compra. También podemos recopilar datos técnicos sobre su
            dispositivo y uso de nuestro sitio web mediante cookies básicas.
          </p>

          <h4 className="font-bold text-gray-900 mt-4" data-id="element-129">
            2. Cómo usamos su información
          </h4>
          <p data-id="element-130">
            Utilizamos sus datos para procesar sus pedidos, entregarle el
            producto digital y mejorar nuestros servicios. No utilizamos su
            información para fines no relacionados con su transacción o la
            mejora de su experiencia.
          </p>

          <h4 className="font-bold text-gray-900 mt-4" data-id="element-131">
            3. Protección de datos
          </h4>
          <p data-id="element-132">
            No vendemos, alquilamos ni compartimos su información personal con
            terceros con fines comerciales. Sus datos están protegidos mediante
            medidas de seguridad estándar de la industria.
          </p>

          <h4 className="font-bold text-gray-900 mt-4" data-id="element-133">4. Sus derechos</h4>
          <p data-id="element-134">
            Usted tiene derecho a acceder, corregir o solicitar la eliminación
            de sus datos personales en cualquier momento.
          </p>

          <h4 className="font-bold text-gray-900 mt-4" data-id="element-135">
            5. Cambios a esta política
          </h4>
          <p data-id="element-136">
            Podemos actualizar esta política ocasionalmente. Cualquier cambio
            será publicado en esta página.
          </p>
        </div>
      </Modal>

      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-137">
        <div className="space-y-4" data-id="element-138">
          <p data-id="element-139">
            <strong data-id="element-140">
              Última actualización: {new Date().toLocaleDateString()}
            </strong>
          </p>
          <p data-id="element-141">
            Al acceder a este sitio web y comprar nuestros productos, usted
            acepta estar sujeto a los siguientes términos y condiciones.
          </p>

          <h4 className="font-bold text-gray-900 mt-4" data-id="element-142">
            1. Descripción del Producto
          </h4>
          <p data-id="element-143">
            "La Solución de 7 Minutos para el Talón Matutino" es un producto
            digital (guía en formato PDF) disponible para descarga inmediata
            después de la compra. No se enviará ningún producto físico.
          </p>

          <h4 className="font-bold text-gray-900 mt-4" data-id="element-144">
            2. Política de Reembolso
          </h4>
          <p data-id="element-145">
            Ofrecemos una garantía de devolución de dinero de 60 días. Si no
            está satisfecho con el producto, puede solicitar un reembolso
            completo dentro de los 60 días posteriores a la compra. El reembolso
            se procesará sin preguntas.
          </p>

          <h4 className="font-bold text-gray-900 mt-4" data-id="element-146">
            3. Propiedad Intelectual
          </h4>
          <p data-id="element-147">
            Todo el contenido, incluyendo texto, gráficos, logotipos e imágenes,
            es propiedad de Morning Heel Fix y está protegido por leyes de
            derechos de autor. No está permitida la reproducción o distribución
            no autorizada.
          </p>

          <h4 className="font-bold text-gray-900 mt-4" data-id="element-148">
            4. Limitación de Responsabilidad
          </h4>
          <p data-id="element-149">
            Este producto es solo para fines informativos y educativos. No
            sustituye el consejo médico profesional. Morning Heel Fix no se hace
            responsable de ninguna lesión o daño que pueda resultar del uso de
            la información proporcionada. Consulte siempre a un médico antes de
            comenzar cualquier rutina de ejercicios.
          </p>

          <h4 className="font-bold text-gray-900 mt-4" data-id="element-150">5. Ley Aplicable</h4>
          <p data-id="element-151">
            Estos términos se regirán e interpretarán de acuerdo con las leyes
            vigentes, sin tener en cuenta sus conflictos de disposiciones
            legales.
          </p>
        </div>
      </Modal>
    </div>;
}